import { useGetSalesRepHistoryQuery, useGetSalesRepQuery, useUpdateSalesRepMutation } from '@/services/salesReps';
import { Badge, Box, Button, Card, Spinner, toast, useOpenClose } from '@palmetto/palmetto-components';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useState } from 'react';
import { Link, NavLink, useParams } from 'react-router-dom';

import { SalesRepStatus, salesRepStatusLabels, UserPermissions } from 'types';

import RequirePermissions from '../auth/requirePermissions';
import DateTimeDisplay from '../DateTime';
import { MainContainer } from '../MainContainer';

import { ChangeSalesRepStatusModal } from './ChangeSalesRepStatusModal';
import { SalesRepActionModal } from './SalesRepActionModal';
import { salesRepStatusBadgeVariant } from './statusBadge';
import { TerminateSalesRepModal } from './TerminateSalesRepModal';

export const SalesRepDetails = () => {
  const { id } = useParams();
  const { data, isLoading } = useGetSalesRepQuery({ id: id as string }, { skip: !id });
  const [updateSalesRep, { isLoading: isUpdatingSalesRep }] = useUpdateSalesRepMutation();
  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();
  const [salesRep, setSalesRep] = useState<any>(data);
  const { salesRepStatusesV2 = false } = useFlags();
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [terminateModalOpen, setTerminateModalOpen] = useState(false);
  const { data: history } = useGetSalesRepHistoryQuery(
    { salesRepId: id as string, limit: 1 },
    { skip: !id || !salesRepStatusesV2 },
  );
  const latestStatusChange = history?.data?.[0];

  const handleOpenModal = useCallback(
    (newStatus: string) => {
      const actionToPerform = newStatus === SalesRepStatus.active ? 'unblock' : 'block';
      setSalesRep({ ...data, actionToPerform, newStatus });
      openModal();
    },
    [data, openModal],
  );

  const handleCloseModal = useCallback(() => {
    setSalesRep(undefined);
    closeModal();
  }, [closeModal]);

  const handleUpdateStatus = useCallback(
    async (status: SalesRepStatus) => {
      if (!salesRep) return;
      const toastId = toast.loading(
        `${salesRep.actionToPerform.charAt(0).toUpperCase() + salesRep.actionToPerform.slice(1)}ing sales rep...`,
      );
      try {
        await updateSalesRep({ id: salesRep.id, status, reason: 'Legacy status change' });
      } catch (e: any) {
        console.error(e);
        toast.error(e?.data?.message || `An error occurred while ${salesRep.actionToPerform}ing the sales rep.`);
      } finally {
        toast.dismiss(toastId);
        handleCloseModal();
      }
    },
    [salesRep, updateSalesRep, handleCloseModal],
  );

  const handleOpenStatusModal = useCallback(() => setStatusModalOpen(true), []);
  const handleCloseStatusModal = useCallback(() => setStatusModalOpen(false), []);
  const handleOpenTerminateModal = useCallback(() => setTerminateModalOpen(true), []);
  const handleCloseTerminateModal = useCallback(() => setTerminateModalOpen(false), []);

  const handleLegacyBlock = useCallback(() => handleOpenModal(SalesRepStatus.blocked), [handleOpenModal]);
  const handleLegacyActivate = useCallback(() => handleOpenModal(SalesRepStatus.active), [handleOpenModal]);

  return (
    <MainContainer>
      <Box style={{ marginLeft: '0' }}>
        <NavLink to={`/admin/sales-reps`} style={{ textDecoration: 'none', fontWeight: '500' }} end>
          {({ isActive }) => (
            <Box
              as="span"
              className={isActive ? 'tab-active' : ''}
              hover={{ color: 'primary' }}
              style={{ transitionDuration: '0s' }}
            >
              Sales Reps
            </Box>
          )}
        </NavLink>
      </Box>
      {isLoading ? (
        <Box
          display="block"
          textAlign="center"
          padding="lg"
          childGap="xl"
          borderColor="separator"
          borderWidth="xs 0 0 0"
        >
          <Spinner size="lg" />
        </Box>
      ) : (
        <Box display="flex" direction={{ desktop: 'row', base: 'column' }} gap="4xl">
          <Card
            width={{
              desktop: '50',
              tablet: '100',
            }}
          >
            <Card.Header childGap="lg">
              <Box alignContent="center">
                <img src={data?.idProviderUser?.photoUrl} alt={data?.idProviderUser?.displayName} />
              </Box>
            </Card.Header>
            <Card.Section>
              <Badge
                variant={salesRepStatusBadgeVariant[data?.status as SalesRepStatus] ?? 'default'}
                message={salesRepStatusLabels[data?.status as SalesRepStatus]}
              />
            </Card.Section>
            {data?.status !== 'override' ? (
              <>
                <Card.Section>
                  <Box direction="column" childGap="2xs">
                    <Box as="h1" fontSize="md">
                      {data?.idProviderUser?.displayName}
                    </Box>
                    {data?.idProviderUser?.displayName !== data?.idProviderUser?.legalName ? (
                      <Box as="p" fontSize="xs">
                        Legal Name: {data?.idProviderUser?.legalName}
                      </Box>
                    ) : null}
                    <Box as="p" fontSize="xs">
                      Recheck Id: {data?.idProviderUser?.idProviderId}
                    </Box>
                  </Box>
                </Card.Section>
                <Card.Section childGap="md">
                  <Box>
                    <Box fontWeight="medium">Recheck Verification:</Box>{' '}
                    <DateTimeDisplay
                      value={data?.idProviderUser?.idProviderVerificationDate as any}
                      displayLocalDateTime
                      dateFormat="MM-dd-yyyy h:mm a"
                    />
                  </Box>
                  <Box>
                    <Box fontWeight="medium">Last Authenticated On:</Box>{' '}
                    <DateTimeDisplay
                      value={data?.authenticatedOn as any}
                      displayLocalDateTime
                      dateFormat="MM-dd-yyyy h:mm a"
                    />
                  </Box>
                </Card.Section>
                <Card.Footer>
                  {salesRepStatusesV2 ? (
                    <Box direction="row" gap="sm">
                      <RequirePermissions permissions={[UserPermissions.reCheckAdmin]}>
                        <Button size="sm" variant="secondary" onClick={handleOpenStatusModal}>
                          Change Status
                        </Button>
                      </RequirePermissions>
                      {data?.status !== SalesRepStatus.terminated ? (
                        <RequirePermissions permissions={[UserPermissions.reCheckAdmin]}>
                          <Button size="sm" variant="secondary" tone="danger" onClick={handleOpenTerminateModal}>
                            Terminate
                          </Button>
                        </RequirePermissions>
                      ) : null}
                    </Box>
                  ) : (
                    <>
                      {data?.status === SalesRepStatus.active ? (
                        <RequirePermissions permissions={[UserPermissions.reCheckAdmin]}>
                          <Button
                            iconPrefix="ban"
                            size="sm"
                            variant="secondary"
                            tone="danger"
                            onClick={handleLegacyBlock}
                          >
                            Block
                          </Button>
                        </RequirePermissions>
                      ) : null}
                      {[
                        SalesRepStatus.blocked,
                        SalesRepStatus.temporarySuspend,
                        SalesRepStatus.suspended,
                        SalesRepStatus.terminated,
                      ].includes(data?.status as SalesRepStatus) ? (
                        <RequirePermissions permissions={[UserPermissions.reCheckAdmin]}>
                          <Button
                            iconPrefix="checkbox-btn-checked"
                            size="sm"
                            variant="secondary"
                            onClick={handleLegacyActivate}
                          >
                            Activate
                          </Button>
                        </RequirePermissions>
                      ) : null}
                    </>
                  )}
                </Card.Footer>
                {salesRepStatusesV2 && latestStatusChange ? (
                  <Card.Section childGap="2xs">
                    <Box fontWeight="medium">Last Status Change:</Box>
                    <DateTimeDisplay
                      value={latestStatusChange.createdAt}
                      displayLocalDateTime
                      dateFormat="MM-dd-yyyy h:mm a"
                    />
                    <Box>
                      <strong>{latestStatusChange.performedBy?.displayName || 'LightReach Support'}</strong> changed
                      status
                    </Box>
                    <Box direction="row" alignItems="center" childGap="xs">
                      <Badge
                        variant={
                          salesRepStatusBadgeVariant[
                            latestStatusChange.data?.diff?.from?.status as SalesRepStatus
                          ] ?? 'default'
                        }
                        message={
                          salesRepStatusLabels[latestStatusChange.data?.diff?.from?.status as SalesRepStatus] ?? '—'
                        }
                      />
                      <Box as="span">→</Box>
                      <Badge
                        variant={
                          salesRepStatusBadgeVariant[latestStatusChange.data?.diff?.to?.status as SalesRepStatus] ??
                          'default'
                        }
                        message={
                          salesRepStatusLabels[latestStatusChange.data?.diff?.to?.status as SalesRepStatus] ?? '—'
                        }
                      />
                    </Box>
                    {latestStatusChange.data?.reason ? (
                      <Box color="body-secondary">Reason: {String(latestStatusChange.data.reason)}</Box>
                    ) : null}
                  </Card.Section>
                ) : null}
              </>
            ) : null}
          </Card>
          <Box direction="column" childGap="md" width="6xl">
            {data?.idProviderUser?.emails?.length ? (
              <Card>
                <Card.Section childGap="md">
                  <Box as="h2" fontSize="lg">
                    Recheck Emails
                  </Box>
                  <Box padding="0 0 lg 0">
                    {data?.idProviderUser?.emails?.map((email: string) => (
                      <ListItemDisplay value={email} key={email} />
                    ))}
                  </Box>
                </Card.Section>
              </Card>
            ) : null}
            <Card>
              <Card.Section childGap="md">
                <Box as="h2" fontSize="lg">
                  LightReach Emails
                </Box>
                {data?.accountEmails?.map((email) => (
                  <ListItemDisplay value={email} key={email} />
                ))}
              </Card.Section>
            </Card>
            <Card>
              <Card.Section childGap="md">
                <Box as="h2" fontSize="lg">
                  LightReach Organizations
                </Box>
                {data?.organizationIds?.map((org) => (
                  <ListItemDisplay value={org} key={org} />
                ))}
              </Card.Section>
            </Card>
            <Card>
              <Card.Section childGap="md">
                <Box as="h2" fontSize="lg">
                  LightReach Accounts
                </Box>
                {data?.accountIds?.map((accountId) => (
                  <ListItemDisplay value={accountId} link={`/accounts/${accountId}`} key={accountId} />
                ))}
              </Card.Section>
            </Card>
          </Box>
        </Box>
      )}
      {!salesRepStatusesV2 && (
        <SalesRepActionModal
          isModalOpen={isModalOpen}
          salesRep={salesRep}
          handleCloseModal={handleCloseModal}
          handleUpdateStatus={handleUpdateStatus}
          isLoading={isLoading || isUpdatingSalesRep}
        />
      )}
      {salesRepStatusesV2 && statusModalOpen && (
        <ChangeSalesRepStatusModal isOpen={statusModalOpen} salesRep={data} onClose={handleCloseStatusModal} />
      )}
      {salesRepStatusesV2 && terminateModalOpen && (
        <TerminateSalesRepModal isOpen={terminateModalOpen} salesRep={data} onClose={handleCloseTerminateModal} />
      )}
    </MainContainer>
  );
};

const ListItemDisplay = ({ value, link }: { value?: string; link?: string }) => (
  <Box direction="row" padding="0 lg" gap="5xl">
    <Box borderWidth="0 0 xs 0" borderColor="separator" flex="auto" direction="row" padding="xs 0">
      {link ? <Link to={link}>{value}</Link> : <Box>{value}</Box>}
    </Box>
  </Box>
);
